import { useCallback, useEffect, useRef, useState } from 'react';
import type { DistributionClient } from '../lib/api';
import type {
  IptvChannel,
  IptvChannelKind,
  IptvGroup,
  IptvSource,
} from '../lib/types';
import { useI18n } from '../lib/use-i18n';
import { IptvAddSource } from './IptvAddSource';

const PAGE_SIZE = 60;
const KINDS: IptvChannelKind[] = ['live', 'movie', 'series'];
/** How often an importing playlist is re-checked for progress. */
const IMPORT_POLL_MS = 4_000;

export function IptvView({ client, query, onOpen }: {
  client: DistributionClient;
  query: string;
  onOpen: (channelId: string) => void;
}) {
  const { t } = useI18n();
  const [sources, setSources] = useState<IptvSource[]>([]);
  const [sourceId, setSourceId] = useState<string | undefined>(undefined);
  const [kind, setKind] = useState<IptvChannelKind>('live');
  const [group, setGroup] = useState<string | undefined>(undefined);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [groups, setGroups] = useState<IptvGroup[]>([]);
  const [channels, setChannels] = useState<IptvChannel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [adding, setAdding] = useState(false);
  const requestVersion = useRef(0);

  const search = query.trim();

  const loadSources = useCallback(async () => {
    const found = await client.iptvSources().catch(() => null);
    if (found) setSources(found.items);
    return found?.items ?? [];
  }, [client]);

  useEffect(() => { void loadSources(); }, [loadSources]);

  // An import runs in the worker; poll while any playlist is still filling up.
  useEffect(() => {
    if (!sources.some((source) => source.state === 'importing' || source.state === 'pending')) {
      return;
    }
    const timer = window.setInterval(() => { void loadSources(); }, IMPORT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadSources, sources]);

  const loadGroups = useCallback(async () => {
    const found = await client.iptvGroups(sourceId, kind).catch(() => null);
    setGroups(found?.items ?? []);
  }, [client, kind, sourceId]);

  const loadChannels = useCallback(async (offset = 0) => {
    const version = ++requestVersion.current;
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError(false);
    try {
      const page = await client.iptvChannels({
        sourceId,
        kind: favoritesOnly ? undefined : kind,
        group,
        q: search || undefined,
        favorites: favoritesOnly,
        limit: PAGE_SIZE,
        offset,
      });
      if (requestVersion.current !== version) return;
      setTotal(page.total);
      setChannels((current) => (offset === 0 ? page.items : [...current, ...page.items]));
    } catch {
      if (requestVersion.current === version) {
        setError(true);
        if (offset === 0) setChannels([]);
      }
    } finally {
      if (requestVersion.current === version) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [client, favoritesOnly, group, kind, search, sourceId]);

  useEffect(() => { void loadGroups(); }, [loadGroups]);

  useEffect(() => {
    const delay = search ? 300 : 0;
    const timer = window.setTimeout(() => void loadChannels(0), delay);
    return () => window.clearTimeout(timer);
  }, [loadChannels, search]);

  async function toggleFavorite(channel: IptvChannel) {
    const result = await client.toggleIptvFavorite(channel.id).catch(() => null);
    if (!result) return;
    setChannels((current) => current.map((item) => (
      item.id === channel.id ? { ...item, favorite: result.favorite } : item
    )));
  }

  const importing = sources.filter((source) => source.state === 'importing' || source.state === 'pending');
  const failed = sources.filter((source) => source.state === 'failed');
  const hasSources = sources.length > 0;

  return (
    <section className="iptv-view">
      <header className="iptv-head">
        <div className="cybertube-title">
          <span>IPTV</span>
          <h1>{search ? t('cybertubeResults') : t('iptvChannels')}</h1>
        </div>
        <button className="cybertube-connect" data-focusable="true" onClick={() => setAdding(true)}>
          {t('iptvAddPlaylist')}
        </button>
      </header>

      {hasSources && (
        <SourceStrip
          sources={sources}
          activeId={sourceId}
          onSelect={(id) => { setSourceId(id); setGroup(undefined); }}
          onRefresh={async (id) => { await client.refreshIptvSource(id).catch(() => undefined); await loadSources(); }}
          onRemove={async (id) => {
            await client.removeIptvSource(id).catch(() => undefined);
            const rest = await loadSources();
            if (!rest.some((source) => source.id === sourceId)) setSourceId(undefined);
            void loadChannels(0);
          }}
        />
      )}

      {importing.map((source) => (
        <p key={source.id} className="iptv-banner">
          {t('iptvImporting', { name: source.name })} · {source.channelCount}
        </p>
      ))}
      {failed.map((source) => (
        <p key={source.id} className="iptv-banner error">
          {source.name}: {source.error ?? t('temporarilyUnavailable')}
        </p>
      ))}

      {!hasSources && !loading ? (
        <div className="empty-state">
          <b>{t('iptvNoPlaylists')}</b>
          <span>{t('iptvNoPlaylistsHint')}</span>
          <button className="primary-action" data-focusable="true" onClick={() => setAdding(true)}>
            {t('iptvAddPlaylist')}
          </button>
        </div>
      ) : (
        <>
          <nav className="cybertube-tabs" aria-label="IPTV">
            {KINDS.map((item) => (
              <button
                key={item}
                type="button"
                data-focusable="true"
                className={`cybertube-tab ${!favoritesOnly && kind === item ? 'active' : ''}`}
                onClick={() => { setFavoritesOnly(false); setKind(item); setGroup(undefined); }}
              >
                {t(item === 'live' ? 'iptvLive' : item === 'movie' ? 'iptvMovies' : 'iptvSeries')}
              </button>
            ))}
            <button
              type="button"
              data-focusable="true"
              className={`cybertube-tab ${favoritesOnly ? 'active' : ''}`}
              onClick={() => { setFavoritesOnly(true); setGroup(undefined); }}
            >
              ★ {t('iptvFavorites')}
            </button>
          </nav>

          {!favoritesOnly && groups.length > 1 && (
            <div className="iptv-groups">
              <button
                type="button"
                data-focusable="true"
                className={`iptv-group ${!group ? 'active' : ''}`}
                onClick={() => setGroup(undefined)}
              >
                {t('iptvAllGroups')}
              </button>
              {groups.map((item) => (
                <button
                  key={item.group ?? '__none'}
                  type="button"
                  data-focusable="true"
                  className={`iptv-group ${group === item.group ? 'active' : ''}`}
                  onClick={() => setGroup(item.group ?? undefined)}
                >
                  {item.group ?? t('iptvUngrouped')} <i>{item.count}</i>
                </button>
              ))}
            </div>
          )}

          {loading && <div className="loading-line" />}

          {channels.length > 0 && (
            <div className="iptv-grid">
              {channels.map((channel) => (
                <ChannelCard
                  key={channel.id}
                  channel={channel}
                  onOpen={() => onOpen(channel.id)}
                  onFavorite={() => void toggleFavorite(channel)}
                />
              ))}
            </div>
          )}

          {!loading && !channels.length && (
            <div className="empty-state">
              <b>{error ? t('temporarilyUnavailable') : t('noResults')}</b>
              <span>{error ? t('tryAgainSoon') : t('cybertubeEmptyHint')}</span>
            </div>
          )}

          {channels.length < total && (
            <button
              className="text-button iptv-more"
              data-focusable="true"
              disabled={loadingMore}
              onClick={() => void loadChannels(channels.length)}
            >
              {loadingMore ? t('loading') : t('iptvShowMore', { count: total - channels.length })}
            </button>
          )}
        </>
      )}

      {adding && (
        <IptvAddSource
          client={client}
          onClose={() => setAdding(false)}
          onAdded={(source) => {
            setAdding(false);
            setSources((current) => [...current.filter((item) => item.id !== source.id), source]);
            void loadSources();
          }}
        />
      )}
    </section>
  );
}

function SourceStrip({ sources, activeId, onSelect, onRefresh, onRemove }: {
  sources: IptvSource[];
  activeId?: string;
  onSelect: (id: string | undefined) => void;
  onRefresh: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="iptv-sources">
      <button
        type="button"
        data-focusable="true"
        className={`iptv-source ${!activeId ? 'active' : ''}`}
        onClick={() => onSelect(undefined)}
      >
        {t('iptvAllPlaylists')}
      </button>
      {sources.map((source) => (
        <span key={source.id} className={`iptv-source-wrap ${activeId === source.id ? 'active' : ''}`}>
          <button
            type="button"
            data-focusable="true"
            className={`iptv-source ${activeId === source.id ? 'active' : ''}`}
            onClick={() => onSelect(source.id)}
            title={source.host ?? undefined}
          >
            {source.name}
            {source.isDefault ? <em className="iptv-source-default">★</em> : null}
            <i>{source.channelCount}</i>
          </button>
          {!source.isDefault ? (
            <>
              <button
                type="button"
                data-focusable="true"
                className="iptv-source-action"
                onClick={() => onRefresh(source.id)}
                aria-label={t('iptvRefresh')}
                title={t('iptvRefresh')}
              >
                ⟳
              </button>
              <button
                type="button"
                data-focusable="true"
                className="iptv-source-action"
                onClick={() => onRemove(source.id)}
                aria-label={t('iptvRemove')}
                title={t('iptvRemove')}
              >
                ×
              </button>
            </>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function ChannelCard({ channel, onOpen, onFavorite }: {
  channel: IptvChannel;
  onOpen: () => void;
  onFavorite: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="iptv-card">
      <button className="iptv-card-main" data-focusable="true" onClick={onOpen}>
        <span className="iptv-logo">
          {channel.logo
            ? <img src={channel.logo} alt="" loading="lazy" />
            : <b>{channel.name.slice(0, 2).toUpperCase()}</b>}
        </span>
        <span className="iptv-card-copy">
          <b>{channel.name}</b>
          {channel.now ? (
            <>
              <small>{channel.now.title}</small>
              <span className="iptv-progress"><i style={{ width: `${channel.now.progress}%` }} /></span>
              {channel.next && <u>{t('iptvNext')}: {channel.next.title}</u>}
            </>
          ) : (
            <small className="muted">{channel.group ?? t('iptvNoGuide')}</small>
          )}
        </span>
      </button>
      <button
        className={`iptv-fav ${channel.favorite ? 'active' : ''}`}
        data-focusable="true"
        onClick={onFavorite}
        aria-label={t('iptvFavorites')}
      >
        ★
      </button>
    </div>
  );
}
